// src/utils/floorPlans.ts
// Types, loader, and helpers for the IMS floor-plans.json data.

// ─── Types ────────────────────────────────────────────────────────────────────
export type Shape =
  | { type: 'rect'; x: number; y: number; w: number; h: number }
  | { type: 'polygon'; points: string };

export interface Room {
  roomId: string;
  name: string;
  type: string;
  capacity?: number;
  area_sqm?: number;
  position?: string;
  shape: Shape;
  labelAnchor: { x: number; y: number };
  door: { x: number; y: number };
  features?: string[];
}

export interface Amenity {
  id: string;
  type: string;
  label: string;
  shape?: Shape;
  position?: { x: number; y: number };
  position_text?: string;
  labelAnchor?: { x: number; y: number };
  restricted?: boolean;
  skip?: boolean;
  note?: string;
}

export interface Entrance {
  id: string;
  type: string;
  label: string;
  position: { x: number; y: number };
  leadsTo?: string;
}

export interface Exit {
  id: string;
  type: string;
  label: string;
  position: { x: number; y: number };
}

export interface Floor {
  id: string;
  level: number;
  label: string;
  shortLabel: string;
  description: string;
  outline: { type: 'rect'; x: number; y: number; w: number; h: number };
  corridor: { axis: string; y: number; from: number; to: number; width: number };
  rooms: Room[];
  amenities: Amenity[];
  stairs: { ref: string; label: string }[];
  entrances?: Entrance[];
  exits?: Exit[];
}

export interface CornerTower {
  id: string;
  side: 'east' | 'west';
  shape: string;
  center: { x: number; y: number };
  radius: number;
}

export interface CentralPavilion {
  side: string;
  footprint: { x: number; y: number; w: number; h: number };
  features: Array<{ type: string; label: string }>;
}

export interface VerticalCirculation {
  id: string;
  label: string;
  type: 'stairs' | 'elevator';
  footprint: { x: number; y: number; w: number; h: number };
  connectsFloors: string[];
  note?: string;
}

export interface Building {
  id: string;
  name: string;
  description: string;
  totalFloors: number;
  dimensions: { width_m: number; depth_m: number };
  scale_unitsPerMeter: number;
  viewBox: { width: number; height: number };
  orientation: { north: string; south: string; east: string; west: string };
  facade: { cornerTowers: CornerTower[]; centralPavilion: CentralPavilion };
}

export interface BuildingData {
  building: Building;
  verticalCirculation: VerticalCirculation[];
  floors: Floor[];
  _notes?: any;
}

// ─── Loader (singleton, cached) ───────────────────────────────────────────────
let cache: BuildingData | null = null;
let loadPromise: Promise<BuildingData> | null = null;

export async function loadFloorPlans(): Promise<BuildingData> {
  if (cache) return cache;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const res = await fetch('/floor-plans.json', { cache: 'no-store' });
      if (!res.ok) throw new Error(`Failed to load floor plans: ${res.status}`);
      const data = (await res.json()) as BuildingData;
      cache = data;
      return data;
    } catch (err) {
      loadPromise = null;
      throw err;
    }
  })();
  return loadPromise;
}

// ─── Lookups ──────────────────────────────────────────────────────────────────
export function findRoom(
  data: BuildingData,
  query: string,
): { room: Room; floor: Floor } | null {
  const q = query.toLowerCase().trim();
  for (const floor of data.floors) {
    const room = floor.rooms.find(
      r => r.roomId.toLowerCase() === q || r.name.toLowerCase() === q,
    );
    if (room) return { room, floor };
  }
  return null;
}

export function searchRooms(
  data: BuildingData,
  query: string,
  floorId?: string,
): Array<{ room: Room; floor: Floor }> {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const results: Array<{ room: Room; floor: Floor }> = [];
  const floors = floorId ? data.floors.filter(f => f.id === floorId) : data.floors;
  for (const floor of floors) {
    for (const room of floor.rooms) {
      if (
        room.name.toLowerCase().includes(q) ||
        room.type.toLowerCase().includes(q) ||
        room.roomId.toLowerCase().includes(q) ||
        (room.position && room.position.toLowerCase().includes(q))
      ) {
        results.push({ room, floor });
      }
    }
  }
  return results;
}

export function getFloor(data: BuildingData, floorId: string): Floor | undefined {
  return data.floors.find(f => f.id === floorId);
}

export function getStair(
  data: BuildingData,
  stairRef: string,
): VerticalCirculation | undefined {
  return data.verticalCirculation.find(s => s.id === stairRef);
}

// ─── Geometry ─────────────────────────────────────────────────────────────────
export function shapeBounds(shape: Shape): { x: number; y: number; w: number; h: number } {
  if (shape.type === 'rect') return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
  const pts = shape.points.trim().split(/\s+/).map(p => p.split(',').map(Number));
  const xs = pts.map(p => p[0]);
  const ys = pts.map(p => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

// ─── Amenity styling ──────────────────────────────────────────────────────────
export const AMENITY_VISUALS: Record<
  string,
  { color: string; icon: string; label: string }
> = {
  restroom:         { color: '#9CA3AF', icon: '🚻', label: 'Restroom'        },
  'water-fountain': { color: '#60A5FA', icon: '💧', label: 'Water Fountain'  },
  lounge:           { color: '#A78BFA', icon: '☕', label: 'Lounge'          },
  kitchen:          { color: '#F59E0B', icon: '🍴', label: 'Kitchenette'     },
  reception:        { color: '#14B8A6', icon: 'ℹ',  label: 'Reception'       },
  lobby:            { color: '#14B8A6', icon: '◉',  label: 'Lobby'           },
  vending:          { color: '#F59E0B', icon: '🥤', label: 'Vending'         },
  printer:          { color: '#9CA3AF', icon: '🖨',  label: 'Printer'         },
  'study-area':     { color: '#A78BFA', icon: '📚', label: 'Study Area'      },
  'notice-board':   { color: '#9CA3AF', icon: '📋', label: 'Notice Board'    },
  rooftop:          { color: '#EF4444', icon: '⚠',  label: 'Rooftop Access'  },
};

// ─── Stable color per floor (for isometric labels) ───────────────────────────
export function floorAccent(floorId: string): string {
  const map: Record<string, string> = {
    basement: '#6B7280',
    ground:   '#14B8A6',
    first:    '#60A5FA',
    second:   '#A78BFA',
    third:    '#F59E0B',
  };
  return map[floorId] ?? '#14B8A6';
}